// src/pages/admin/members/MemberShared.tsx
//
// Every constant, primitive, and reusable component shared across:
//   AdminAllMembers.tsx  AdminLegacyFellows.tsx
//   AdminInnerCircle.tsx  AdminRegionalReps.tsx
//   AdminMembers.tsx  (layout wrapper / tab router)
//
// Nothing in this file renders a full page.

import React, { useState, useCallback } from "react";
import { X, Check, AlertCircle, Loader2, Shield, Flame } from "lucide-react";
import { QuarterKey } from "../../app/api/programmeApi";
import { MemberTier, UserRole } from "../../app/api/authApi";
import { FellowsStatus } from "../../app/api/fellowsApi";
import {
  ManagedUser,
  UpdateUserRequest,
  useAdminAdvanceMenteeQuarterMutation,
  useAdminSetMenteeTierMutation,
  useUpdateUserMutation,
} from "../../app/api/adminApi";

/* ─────────────────────────────────────────────────────────────────
   ROLE META  — covers every possible role value in the DB.
   Any role not listed here falls back safely so
   the UI never crashes on an unexpected role value.
───────────────────────────────────────────────────────────────── */

export const ROLE_META: Record<string, { label: string; colour: string }> = {
  admin: { label: "Super Admin", colour: "bg-red-100 text-red-700" },
  committee_chair: {
    label: "Committee Chair",
    colour: "bg-purple-100 text-purple-700",
  },
  committee_member: {
    label: "Committee Member",
    colour: "bg-blue-100 text-blue-700",
  },
  portal_manager: {
    label: "Portal Manager",
    colour: "bg-green-100 text-green-700",
  },
  regional_rep: {
    label: "Regional Rep",
    colour: "bg-amber-100 text-amber-700",
  },
  co: {
    label: "Continental Overseer",
    colour: "bg-yellow-100 text-yellow-700",
  },
  cyp: {
    label: "Continental YP",
    colour: "bg-teal-100 text-teal-700",
  },
  ryp: {
    label: "Regional YP",
    colour: "bg-orange-100 text-orange-700",
  },
  pyp: {
    label: "Provincial YP",
    colour: "bg-lime-100 text-lime-700",
  },
  mentor: {
    label: "Mentor",
    colour: "bg-cyan-100 text-cyan-700",
  },
  mentee: {
    label: "Mentee",
    colour: "bg-gray-100 text-gray-600",
  },
};

/** Safe accessor — always returns a meta object. */
export const getRoleMeta = (
  role?: string | null,
): { label: string; colour: string } =>
  (role ? ROLE_META[role] : undefined) ?? {
    label: role ?? "Unknown",
    colour: "bg-gray-100 text-gray-500",
  };

/* ─────────────────────────────────────────────────────────────────
   ROLE ACCESS CONSTANTS
───────────────────────────────────────────────────────────────── */
export const CAN_VIEW_USERS: UserRole[] = [
  "admin",
  "committee_chair",
  "co",
  "cyp",
  "ryp",
];
export const CAN_EDIT_USERS: UserRole[] = ["admin", "committee_chair"];
export const CAN_CHANGE_ROLE: UserRole[] = ["admin"];
export const CAN_DEACTIVATE: UserRole[] = ["admin"];
export const CAN_ADVANCE_Q: UserRole[] = [
  "admin",
  "committee_chair",
  "committee_member",
  "portal_manager",
];
export const CAN_SET_TIER: UserRole[] = ["admin"];
export const CAN_VIEW_FELLOWS: UserRole[] = [
  "admin",
  "committee_chair",
  "regional_rep",
  "co",
  "cyp",
  "ryp",
];
export const CAN_SELECT_FELLOW: UserRole[] = [
  "admin",
  "committee_chair",
  "co",
  "cyp",
];
export const CAN_NOMINATE: UserRole[] = ["admin", "regional_rep"];
export const CAN_VIEW_REPS: UserRole[] = ["admin", "co", "cyp"];
export const CAN_VIEW_IC: UserRole[] = [
  "admin",
  "committee_chair",
  "co",
  "cyp",
  "ryp",
];

/* ─────────────────────────────────────────────────────────────────
   DISPLAY CONSTANTS
───────────────────────────────────────────────────────────────── */
export const TIER_META: Record<MemberTier, { label: string; colour: string }> =
  {
    wider_audience: {
      label: "Wider Audience",
      colour: "bg-green-100 text-green-700",
    },
    inner_circle: {
      label: "Inner Circle",
      colour: "bg-blue-100 text-blue-700",
    },
    legacy_fellows: {
      label: "Legacy Fellows",
      colour: "bg-purple-100 text-purple-700",
    },
  };

/** Safe accessor — always returns a meta object. */
export const getTierMeta = (
  tier?: string | null,
): { label: string; colour: string } =>
  (tier ? TIER_META[tier as MemberTier] : undefined) ?? {
    label: "—",
    colour: "bg-gray-100 text-gray-400",
  };

export const FELLOWS_STATUS_META: Record<
  FellowsStatus,
  { label: string; colour: string; dot: string }
> = {
  nominated: {
    label: "Nominated",
    colour: "bg-amber-100 text-amber-700",
    dot: "bg-amber-400",
  },
  shortlisted: {
    label: "Shortlisted",
    colour: "bg-blue-100 text-blue-700",
    dot: "bg-blue-400",
  },
  selected: {
    label: "Selected",
    colour: "bg-green-100 text-green-700",
    dot: "bg-green-500",
  },
  declined: {
    label: "Declined",
    colour: "bg-red-100 text-red-700",
    dot: "bg-red-400",
  },
  completed: {
    label: "Completed",
    colour: "bg-purple-100 text-purple-700",
    dot: "bg-purple-400",
  },
};

export const QUARTERS: QuarterKey[] = ["Q1", "Q2", "Q3", "Q4"];
export const PAGE_SIZE = 20;

/* ─────────────────────────────────────────────────────────────────
   PRIMITIVE UI COMPONENTS
───────────────────────────────────────────────────────────────── */
export const Bone: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`animate-pulse rounded-xl bg-gray-100 ${className}`} />
);

export const FL: React.FC<{
  children: React.ReactNode;
  required?: boolean;
}> = ({ children, required }) => (
  <label className="block text-xs font-semibold text-gray-700 mb-1">
    {children}
    {required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

export const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (
  p,
) => (
  <input
    {...p}
    className={`w-full text-sm px-3 py-2 rounded-lg border border-gray-200 bg-white
      focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent
      placeholder-gray-400 transition disabled:bg-gray-50 disabled:text-gray-400 ${p.className ?? ""}`}
  />
);

export const Textarea: React.FC<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
> = (p) => (
  <textarea
    {...p}
    className={`w-full text-sm px-3 py-2 rounded-lg border border-gray-200 bg-white
      focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent
      placeholder-gray-400 transition resize-none ${p.className ?? ""}`}
  />
);

export const Sel: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = (
  p,
) => (
  <select
    {...p}
    className={`w-full text-sm px-3 py-2 rounded-lg border border-gray-200 bg-white
      focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent
      transition disabled:bg-gray-50 ${p.className ?? ""}`}
  />
);

export const fmtDate = (d?: string | null) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

export const mkInitials = (u: { firstName: string; lastName: string }) =>
  `${u.firstName[0] ?? "?"}${u.lastName[0] ?? "?"}`.toUpperCase();

/* ─────────────────────────────────────────────────────────────────
   AVATAR
───────────────────────────────────────────────────────────────── */
export const Avatar: React.FC<{
  user: { firstName: string; lastName: string };
  size?: "sm" | "md" | "lg";
  colour?: string;
}> = ({ user, size = "md", colour = "bg-purple-100 text-purple-700" }) => {
  const dims =
    size === "sm"
      ? "w-8 h-8 text-xs"
      : size === "lg"
        ? "w-12 h-12 text-sm"
        : "w-9 h-9 text-xs";

  return (
    <div
      className={`${dims} ${colour} rounded-full flex items-center justify-center font-bold shrink-0`}
    >
      {mkInitials(user)}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   STAT TILE
───────────────────────────────────────────────────────────────── */
export const StatTile: React.FC<{
  label: string;
  value: number | string;
  colour: string;
  icon: React.ReactNode;
  loading?: boolean;
}> = ({ label, value, colour, icon, loading }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-4">
    <div className="flex items-center gap-1.5 mb-1 text-[11px] text-gray-400">
      {icon}
      {label}
    </div>
    {loading ? (
      <Bone className="h-7 w-10 mt-1" />
    ) : (
      <p className={`text-2xl font-bold ${colour}`}>{value}</p>
    )}
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   CONFIRM MODAL
───────────────────────────────────────────────────────────────── */
export const ConfirmModal: React.FC<{
  title: string;
  message: string;
  confirmLabel: string;
  danger?: boolean;
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  children?: React.ReactNode;
}> = ({
  title,
  message,
  confirmLabel,
  danger,
  isLoading,
  onConfirm,
  onCancel,
  children,
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
    <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
      <h3 className="text-sm font-bold text-gray-900 mb-1">{title}</h3>
      <p className="text-xs text-gray-500 mb-4">{message}</p>
      {children}
      <div className="flex gap-3 mt-4">
        <button
          onClick={onCancel}
          className="flex-1 py-2 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={isLoading}
          className={`flex-1 py-2 text-sm font-semibold rounded-lg disabled:opacity-50 transition-colors
            ${danger ? "bg-red-600 text-white hover:bg-red-700" : "bg-purple-600 text-white hover:bg-purple-700"}`}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-1.5">
              <Loader2 size={13} className="animate-spin" />
              Working…
            </span>
          ) : (
            confirmLabel
          )}
        </button>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   EDIT MEMBER PANEL
───────────────────────────────────────────────────────────────── */
interface EditForm {
  region: string;
  province: string;
  ministryRole: string;
  managedRegion: string;
  role: UserRole;
  tier: MemberTier;
}

export const EditMemberPanel: React.FC<{
  member: ManagedUser;
  callerRole: UserRole;
  onClose: () => void;
}> = ({ member, callerRole, onClose }) => {
  const [form, setForm] = useState<EditForm>({
    region: member.region ?? "",
    province: member.province ?? "",
    ministryRole: member.ministryRole ?? "",
    managedRegion: member.managedRegion ?? "",
    role: member.role,
    tier: member.tier,
  });
  const [apiErr, setApiErr] = useState<string | null>(null);
  const [advanceErr, setAdvanceErr] = useState<string | null>(null);
  const [showAdvanceConfirm, setShowAdvanceConfirm] = useState(false);
  const [advanceReason, setAdvanceReason] = useState("");

  const set = useCallback(
    <K extends keyof EditForm>(k: K, v: EditForm[K]) =>
      setForm((p) => ({ ...p, [k]: v })),
    [],
  );

  const [updateUser, { isLoading: isSaving }] = useUpdateUserMutation();
  const [advanceQ, { isLoading: isAdvancing }] =
    useAdminAdvanceMenteeQuarterMutation();
  const [setTierMut, { isLoading: isSettingTier }] =
    useAdminSetMenteeTierMutation();

  const canEditBasic = CAN_EDIT_USERS.includes(callerRole);
  const canChangeRole = CAN_CHANGE_ROLE.includes(callerRole);
  const canAdvance =
    CAN_ADVANCE_Q.includes(callerRole) && member.role === "mentee";
  const canForceTier =
    CAN_SET_TIER.includes(callerRole) && member.role === "mentee";

  const roleMeta: { label: string; colour: string } = getRoleMeta(member.role);
  const tierMeta: { label: string; colour: string } = getTierMeta(member.tier);

  const handleSave = async () => {
    setApiErr(null);
    try {
      const payload: UpdateUserRequest = {
        region: form.region || undefined,
        province: form.province || undefined,
        ministryRole: form.ministryRole || undefined,
      };

      if (canChangeRole && form.role !== member.role) payload.role = form.role;
      if (member.role === "regional_rep") {
        payload.managedRegion = form.managedRegion || undefined;
      }
      if (canEditBasic && form.tier !== member.tier && !canForceTier) {
        payload.tier = form.tier;
      }

      await updateUser({ id: member.id, ...payload }).unwrap();
      onClose();
    } catch (err: any) {
      setApiErr(err?.data?.error ?? "Something went wrong.");
    }
  };

  const handleForceTier = async () => {
    setApiErr(null);
    try {
      await setTierMut({ menteeId: member.id, tier: form.tier }).unwrap();
      onClose();
    } catch (err: any) {
      setApiErr(err?.data?.error ?? "Could not update tier.");
    }
  };

  const handleAdvance = async () => {
    setAdvanceErr(null);
    try {
      await advanceQ({
        menteeId: member.id,
        reason: advanceReason || undefined,
      }).unwrap();
      setShowAdvanceConfirm(false);
      onClose();
    } catch (err: any) {
      setAdvanceErr(err?.data?.error ?? "Could not advance quarter.");
    }
  };

  const profile = member.profile as any;

  return (
    <>
      <div className="fixed inset-0 z-40 flex justify-end">
        <div className="flex-1 bg-black/40" onClick={onClose} />
        <div className="w-full max-w-lg bg-white shadow-2xl flex flex-col overflow-hidden">
          <div className="shrink-0 px-6 py-4 border-b flex items-center gap-3 bg-white sticky top-0 z-10">
            <Avatar user={member} size="md" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900 truncate">
                {member.firstName} {member.lastName}
              </p>
              <p className="text-[11px] text-gray-400 truncate">
                {member.email}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 shrink-0"
            >
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
            {member.role === "mentee" && (
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400 mb-3">
                  Programme status
                </p>
                <div className="grid grid-cols-2 gap-3 text-[11px]">
                  {[
                    {
                      label: "Current quarter",
                      value: profile?.currentQuarter ?? "Q1",
                    },
                    {
                      label: "Quarters done",
                      value: profile?.quartersCompleted ?? 0,
                    },
                    {
                      label: "Avg. score",
                      value:
                        profile?.overallAvgScore != null
                          ? `${profile.overallAvgScore}/10`
                          : "—",
                    },
                    {
                      label: "Tasks submitted",
                      value: profile?.totalTasksSubmitted ?? 0,
                    },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <p className="text-gray-400">{label}</p>
                      <p className="font-semibold text-gray-800">
                        {String(value)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <span
                className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full ${roleMeta.colour}`}
              >
                <Shield size={10} />
                {roleMeta.label}
              </span>

              {member.role === "mentee" && (
                <span
                  className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full ${tierMeta.colour}`}
                >
                  {tierMeta.label}
                </span>
              )}

              <span
                className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full ${
                  member.isActive
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-600"
                }`}
              >
                {member.isActive ? "Active" : "Inactive"}
              </span>

              {member.isEmailVerified && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full bg-blue-50 text-blue-600">
                  <Check size={10} /> Verified
                </span>
              )}

              {member.fellowsEligible && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-600">
                  <Flame size={9} /> Fellows eligible
                </span>
              )}
            </div>

            {canEditBasic ? (
              <div className="space-y-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400">
                  Profile
                </p>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <FL>Region</FL>
                    <Input
                      placeholder="e.g. West Africa — Lagos"
                      value={form.region}
                      onChange={(e) => set("region", e.target.value)}
                    />
                  </div>

                  <div>
                    <FL>Province</FL>
                    <Input
                      placeholder="e.g. Lagos Province"
                      value={form.province}
                      onChange={(e) => set("province", e.target.value)}
                    />
                  </div>

                  <div className="col-span-2">
                    <FL>Ministry role</FL>
                    <Input
                      placeholder="e.g. YAYA Provincial Secretary"
                      value={form.ministryRole}
                      onChange={(e) => set("ministryRole", e.target.value)}
                    />
                  </div>

                  {member.role === "regional_rep" && (
                    <div className="col-span-2">
                      <FL>Managed region</FL>
                      <Input
                        placeholder="The region this rep oversees"
                        value={form.managedRegion}
                        onChange={(e) => set("managedRegion", e.target.value)}
                      />
                    </div>
                  )}
                </div>

                {canChangeRole && (
                  <div>
                    <FL>
                      Role{" "}
                      <span className="font-normal text-red-500 text-[10px]">
                        (Admin only)
                      </span>
                    </FL>
                    <Sel
                      value={form.role}
                      onChange={(e) => set("role", e.target.value as UserRole)}
                    >
                      {Object.entries(ROLE_META).map(([r, m]) => (
                        <option key={r} value={r}>
                          {m.label}
                        </option>
                      ))}
                    </Sel>
                  </div>
                )}

                {member.role === "mentee" && (
                  <div>
                    <FL>Membership tier</FL>
                    <Sel
                      value={form.tier}
                      onChange={(e) =>
                        set("tier", e.target.value as MemberTier)
                      }
                    >
                      {Object.entries(TIER_META).map(([t, m]) => (
                        <option key={t} value={t}>
                          {m.label}
                        </option>
                      ))}
                    </Sel>

                    {canForceTier && form.tier !== member.tier && (
                      <p className="text-[11px] text-amber-600 mt-1 flex items-center gap-1">
                        <AlertCircle size={10} /> Will be force-set via admin
                        endpoint on save.
                      </p>
                    )}
                  </div>
                )}

                {apiErr && (
                  <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    <AlertCircle size={12} className="shrink-0" />
                    {apiErr}
                  </div>
                )}

                <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={
                      canForceTier && form.tier !== member.tier
                        ? handleForceTier
                        : handleSave
                    }
                    disabled={isSaving || isSettingTier}
                    className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-purple-600 text-white rounded-lg hover:bg-purple-700 active:scale-95 transition-all shadow-sm disabled:opacity-50"
                  >
                    {isSaving || isSettingTier ? (
                      <>
                        <Loader2 size={13} className="animate-spin" /> Saving…
                      </>
                    ) : (
                      <>
                        <Check size={13} /> Save changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {[
                  { label: "Region", value: member.region },
                  { label: "Province", value: member.province },
                  { label: "Ministry role", value: member.ministryRole },
                  { label: "Joined", value: fmtDate(member.createdAt) },
                  { label: "Last login", value: fmtDate(member.lastLoginAt) },
                ].map(({ label, value }) => (
                  <div
                    key={label}
                    className="flex items-center justify-between"
                  >
                    <span className="text-xs text-gray-400">{label}</span>
                    <span className="text-xs font-medium text-gray-700">
                      {value || "—"}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {canAdvance && (
              <div className="border-t border-gray-100 pt-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400 mb-2">
                  Quarter progression
                </p>

                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-blue-800">
                      Currently on {profile?.currentQuarter ?? "Q1"}
                    </span>
                    <span className="text-[11px] text-blue-500">
                      {profile?.quartersCompleted ?? 0} completed
                    </span>
                  </div>

                  <p className="text-[11px] text-blue-600 mb-3">
                    Admin advancement bypasses the "all tasks graded"
                    requirement.
                  </p>

                  {advanceErr && (
                    <p className="text-[11px] text-red-600 mb-2 flex items-center gap-1">
                      <AlertCircle size={10} />
                      {advanceErr}
                    </p>
                  )}

                  <button
                    onClick={() => setShowAdvanceConfirm(true)}
                    disabled={isAdvancing}
                    className="w-full text-xs font-semibold py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {isAdvancing ? "Advancing…" : "Advance to next quarter"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showAdvanceConfirm && (
        <ConfirmModal
          title={`Advance ${member.firstName} to next quarter?`}
          message="This bypasses the task-graded requirement. The mentee moves to the next quarter immediately."
          confirmLabel="Advance"
          isLoading={isAdvancing}
          onConfirm={handleAdvance}
          onCancel={() => setShowAdvanceConfirm(false)}
        >
          <div className="mb-1">
            <FL>
              Reason{" "}
              <span className="font-normal text-gray-400">(optional)</span>
            </FL>
            <Input
              placeholder="Admin override reason…"
              value={advanceReason}
              onChange={(e) => setAdvanceReason(e.target.value)}
            />
          </div>
        </ConfirmModal>
      )}
    </>
  );
};